/**
 * ⚠️ FORKED COMPONENT — DO NOT MERGE BACK ⚠️
 *
 * LR DIRECT — 3-Column Inline Bidding Panel
 * Matches mockup: Left (Trip+Equipment), Middle (Bid Action), Right (Monetization)
 *
 * RULES:
 * - Only use direct_loads, direct_load_stops, direct_bids
 * - Do NOT import from Search Loads
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format, differenceInCalendarDays, differenceInMinutes, differenceInSeconds } from "date-fns";
import {
  MapPin, Calendar, Truck, Weight, Package, Route, Clock,
  AlertTriangle, Send, ExternalLink, CheckCircle2, XCircle, Loader2,
  Lock, Coins, CircleDot, MoreHorizontal, Search,
  Zap, Timer, Users, FileText, MessageSquare,
  ShieldCheck, DollarSign, TrendingUp, Activity, BarChart3,
  History, ClipboardCheck, Check, Building2,
  Award, FileCheck2, Download, Share2, ArrowRight,
} from "lucide-react";
import { useDirectShipperActivity } from "@/hooks/useDirectShipperActivity";
import { useDirectShipperCredit } from "@/hooks/useDirectShipperCredit";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useDualCredits } from "@/hooks/useDualCredits";
import { useBillingMode } from "@/hooks/useBillingMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DemoHint } from "@/components/demo/DemoHint";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { DirectLoad } from "./DirectResultsTable";
import { RouteTrackerBar } from "./RouteTrackerBar";
import { LRDirectLoadBriefPlayer } from "./LRDirectLoadBriefPlayer";
import { PrivateNetworkBadge } from "./PrivateNetworkBadge";
import { useBrokerMemberships } from "@/hooks/useBrokerMemberships";
import AtlasLoadAnalysisCard, { parseAtlasAnalysis } from "./AtlasLoadAnalysisCard";
import DirectQuoteDetailView from "./DirectQuoteDetailView";
import RetractReasonDialog from "./RetractReasonDialog";
import { useNetworkShipperName } from "@/hooks/useNetworkShipperName";
import { useEnsureAtlasLoadAnalysis } from "@/hooks/useEnsureAtlasLoadAnalysis";
import { BidActionGate } from "./BidActionGate";
import surelineLogo from "@/assets/sureline-logo.png";
import pistoresiAd1 from "@/assets/pistoresi-ad-1.png.asset.json";
import pistoresiAd2 from "@/assets/pistoresi-ad-2.jpg.asset.json";
const PISTORESI_ADS = [pistoresiAd1, pistoresiAd2];

import { formatEquipmentType } from "@/lib/equipmentTypes";
import { logSurelineClick } from "@/lib/logSurelineClick";
import { useVerificationWarning } from "@/hooks/useVerificationWarning";
import { useResolvedLoadMiles } from "@/hooks/useResolvedLoadMiles";
import { VerificationWarningModal } from "@/components/verification/VerificationWarningModal";

/* ─── Types ──────────────────────────────────────────────── */
interface DirectStop {
  id: string;
  stop_number: number;
  stop_type: string;
  city: string;
  state: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  scheduled_date: string;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  name: string;
  instructions: string | null;
  // Private dispatch — populated by get_direct_load_dispatch RPC
  // only for shipper-owner, awarded broker, or admin
  pickup_number: string | null;
  pallet_count: number | null;
  confidential_notes: string | null;
  is_unlocked: boolean;
}

interface ExistingBid {
  id: string;
  bid_amount: number;
  message: string | null;
  status: string;
  submitted_at: string;
  withdraw_reason?: string | null;
}


/* ─── Urgency helpers ────────────────────────────────────── */

function getPickupUrgency(pickupDate: string): { label: string; isUrgent: boolean } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const pickup = new Date(pickupDate + "T00:00:00");
  const diff = differenceInCalendarDays(pickup, today);
  if (diff <= 0) return { label: "Today", isUrgent: true };
  if (diff === 1) return { label: "Tomorrow", isUrgent: true };
  if (diff <= 3) return { label: "This Week", isUrgent: false };
  return { label: format(pickup, "MMM d"), isUrgent: false };
}

function getDeliveryLabel(deliveryDate: string | null, pickupDate: string): string {
  if (!deliveryDate) return "—";
  const pickup = new Date(pickupDate + "T00:00:00");
  const delivery = new Date(deliveryDate + "T00:00:00");
  const diff = differenceInCalendarDays(delivery, pickup);
  if (diff === 0) return "Same Day";
  if (diff === 1) return "Next Day";
  return format(delivery, "MMM d");
}

function getTimeWindow(stop: DirectStop | undefined): string | null {
  if (!stop) return null;
  const start = stop.scheduled_time_start;
  const end = stop.scheduled_time_end;
  if (start && end) return `${start}–${end}`;
  if (start) return start;
  return null;
}

/* Plain number inputs — no browser up/down spinner arrows. */
const NO_SPINNER =
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0";

/* ─── Competitive position helper ────────────────────────── */
function getCompetitivePosition(
  bidAmount: number,
  rateMin: number | null,
  rateMax: number | null,
): { level: number; label: string } {
  // No shipper target range posted → we genuinely can't score the bid yet.
  if (!rateMin && !rateMax) return { level: -1, label: "Bid to find out" };
  const min = rateMin ?? (rateMax! * 0.8);
  const max = rateMax ?? (rateMin! * 1.2);
  const range = max - min || 1;
  // Lower bid = more competitive
  const normalized = (bidAmount - min) / range;
  if (normalized <= 0.2) return { level: 4, label: "Very Strong" };
  if (normalized <= 0.5) return { level: 3, label: "Competitive" };
  if (normalized <= 0.8) return { level: 2, label: "Average" };
  if (normalized <= 1.0) return { level: 1, label: "Weak" };
  return { level: 0, label: "Too High" };
}

const positionColors = [
  "bg-destructive",       // 0 - too high
  "bg-orange-400",        // 1 - weak
  "bg-amber-400",         // 2 - average
  "bg-emerald-400",       // 3 - competitive
  "bg-emerald-600",       // 4 - very strong
];

/* ─── Props ──────────────────────────────────────────────── */
interface DirectLoadDetailPanelProps {
  load: DirectLoad;
}

/* ═══════════════════════════════════════════════════════════ */

/* ─── Shipper credit rating widget — per-shipper, admin-set values ───
   Reads the rating stored on the LOAD'S shipper company via a
   SECURITY DEFINER RPC (numbers only, non-identifying). Shippers with no
   stored rating fall back to the previous reference values. */
function ShipperCreditRatingWidget({ loadId }: { loadId: string }) {
  const { rating } = useDirectShipperCredit(loadId);
  const score = rating.score;
  const bgClass =
    score >= 90
      ? "bg-[rgba(220,252,231,0.15)]" // subtle green tint
      : score >= 70
        ? "bg-[rgba(254,249,195,0.15)]" // subtle yellow tint
        : "bg-[rgba(254,226,226,0.15)]"; // subtle red tint

  return (
    <div className={cn("rounded-xl border border-black/10 p-3", bgClass)}>
      <div className="mb-2 flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 text-[#02857E]" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Shipper credit rating
        </span>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <div className="h-2.5 w-full rounded-full bg-gradient-to-r from-red-500 via-amber-400 to-[#02857E]" />
          <div className="absolute top-1/2 h-3.5 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-black shadow-sm"
            style={{ left: `${Math.min(100, Math.max(0, score))}%` }} />
        </div>
        <div className="text-right">
          <div className="text-xl font-bold leading-none text-foreground">{rating.grade} / {rating.score}</div>
          <div className="text-[11px] font-semibold leading-none text-[#02857E]">{rating.label}</div>
        </div>
      </div>

      <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-4 gap-1.5 border-t border-black/10 pt-2">
        <div className="min-w-0">
          <div className="text-[9px] font-medium uppercase leading-tight tracking-wider text-muted-foreground">Avg Days to Pay</div>
          <div className="mt-0.5 text-xs font-bold text-foreground">{rating.daysToPay}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] font-medium uppercase leading-tight tracking-wider text-muted-foreground">Payment Trend</div>
          <div className="mt-0.5 text-xs font-bold text-[#02857E]">{rating.paymentTrend}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] font-medium uppercase leading-tight tracking-wider text-muted-foreground">Recommended Exposure</div>
          <div className="mt-0.5 text-xs font-bold text-foreground">{rating.recommendedExposure}</div>
        </div>
        <div className="min-w-0">
          <div className="text-[9px] font-medium uppercase leading-tight tracking-wider text-muted-foreground">Reported Payments</div>
          <div className="mt-0.5 text-xs font-bold text-foreground">{rating.reportedPayments}</div>
        </div>
      </div>

      <p className="mt-2 text-[10px] leading-tight text-muted-foreground/80">
        For reference only. Not a guarantee of payment. Brokers should conduct their own credit verification before extending credit. LaneRunner is not responsible for credit decisions.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
export function DirectLoadDetailPanel({ load }: DirectLoadDetailPanelProps) {
  const { user, profile, isAdmin, isDemoUser } = useAuth();
  const [pistoresiAd] = useState(() => PISTORESI_ADS[Math.floor(Math.random() * PISTORESI_ADS.length)]);
  /* Is this broker already an accepted member of the shipper's private network?
     If so, they already know who the shipper is — the "hidden until you win"
     privacy note is wrong for them. */
  const { data: brokerMemberships } = useBrokerMemberships(user?.id ?? null);
  const inShipperPrivateNetwork = useMemo(() => {
    const netId = (load as any)?.private_network_id;
    if (!netId || !brokerMemberships?.length) return false;
    return brokerMemberships.some(
      (m) => m.network_id === netId || m.shipper_network_id === netId,
    );
  }, [brokerMemberships, load]);
  const navigate = useNavigate();
  const { showWarningOrRun, modalProps } = useVerificationWarning();
  const { companyId } = useDualCredits();

  /* Bid insights are free — no credit gating helper needed. */

  /* ── Quote-post mode (post_type='quote') ─────────────── */
  const isQuote = (load as any).post_type === "quote";

  /* ── Background-fetched state ─────────────────────────── */
  const [stops, setStops] = useState<DirectStop[]>([]);
  const [stopsLoading, setStopsLoading] = useState(true);
  const [existingBid, setExistingBid] = useState<ExistingBid | null>(null);
  const [existingQuote, setExistingQuote] = useState<{ id: string; rate: number; capacity_note: string | null; status: string; created_at: string; withdraw_reason?: string | null } | null>(null);
  const [bidLoading, setBidLoading] = useState(true);
  const [bidCount, setBidCount] = useState<number>(0);

  /* ── Bid form state ───────────────────────────────────── */
  const [bidAmount, setBidAmount] = useState(() => {
    if (load.target_rate_max) return String(load.target_rate_max);
    if (load.target_rate_min) return String(load.target_rate_min);
    return "";
  });
  const [submitting, setSubmitting] = useState(false);
  const [readyToCover, setReadyToCover] = useState(false);
  const [truckInArea, setTruckInArea] = useState(false);
  const [teamAvailable, setTeamAvailable] = useState(false);
  const [bidMessage, setBidMessage] = useState("");
  const [etaHours, setEtaHours] = useState("");
  const [bidMode, setBidMode] = useState<"per_load" | "per_mile">("per_load");

  /* ── Bid insight state (free for all brokers) ─────────── */
  const [unlockedBidRange, setUnlockedBidRange] = useState<{ min: number; max: number } | null>(null);
  const [unlockedExactCount, setUnlockedExactCount] = useState<number | null>(null);
  /* Aggregate-only bid market stats (no identities) from get_direct_bid_market */
  const [bidMarket, setBidMarket] = useState<{
    count: number; min: number | null; max: number | null;
    median: number | null; p25: number | null; p75: number | null;
  } | null>(null);

  const [bidRangeUnlocked, setBidRangeUnlocked] = useState(false);
  const [exactCountUnlocked, setExactCountUnlocked] = useState(false);

  type WinProb =
    | { mode: "pre_bid"; total_bids: number; min_bid: number | null; max_bid: number | null; median_bid: number | null; remaining_slots: number; max_slots: number }
    | { mode: "scored"; score: number; bid_amount: number; total_bids: number; remaining_slots: number; max_slots: number };
  const [winProb, setWinProb] = useState<WinProb | null>(null);
  const [winProbUnlocked, setWinProbUnlocked] = useState(false);

  /* ── Realtime new bid flash ───────────────────────────── */
  const [flashDots, setFlashDots] = useState(false);
  const [recentBidCount, setRecentBidCount] = useState(0);
  const [lastBidTime, setLastBidTime] = useState<Date | null>(null);

  /* ── Live countdown state ─────────────────────────────── */
  const [countdownStr, setCountdownStr] = useState<string | null>(null);
  const [countdownUrgent, setCountdownUrgent] = useState(false);
  // 'normal' (>6h) → teal, 'warn' (≤6h) → amber/yellow, 'critical' (≤1h) → red
  const [countdownLevel, setCountdownLevel] = useState<"normal" | "warn" | "critical">("normal");

  /* ── Background fetches — non-blocking ───────────────── */
  useEffect(() => {
    let cancelled = false;

    supabase
      .rpc("get_direct_load_dispatch" as any, { p_load_id: load.id })
      .then(({ data }) => {
        if (!cancelled && data) setStops(data as unknown as DirectStop[]);
        if (!cancelled) setStopsLoading(false);
      });


    supabase
      .from("direct_bids" as any)
      .select("id, submitted_at", { count: "exact", head: false })
      .eq("direct_load_id", load.id)
      .in("status", ["pending", "accepted"])
      .then(({ data, count }) => {
        if (!cancelled && count !== null) setBidCount(count);
        if (!cancelled && data) {
          const tenMinAgo = new Date(Date.now() - 10 * 60_000);
          const sorted = (data as any[]).sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime());
          const recent = sorted.filter(b => new Date(b.submitted_at) > tenMinAgo).length;
          setRecentBidCount(recent);
          if (sorted.length > 0) setLastBidTime(new Date(sorted[0].submitted_at));
        }
      });

    if (user) {
      if (isQuote) {
        supabase
          .from("direct_quote_responses" as any)
          .select("id, rate, capacity_note, status, created_at, withdraw_reason")
          .eq("direct_load_id", load.id)
          .eq("broker_user_id", user.id)
          .in("status", ["active", "awarded"])
          .order("created_at", { ascending: false })
          .maybeSingle()
          .then(({ data }) => {
            if (!cancelled && data) setExistingQuote(data as any);
            if (!cancelled) setBidLoading(false);
          });
      } else {
        // Only an ACTIVE bid (pending / accepted-awarded) should lock the bid form.
        // Withdrawn / rejected / lost / expired rows must not be treated as
        // "already bid" — the server RPC dedups on status='pending' only, so
        // the UI must match or brokers get a false "already bid" state and
        // no bid form. Order desc + limit 1 defends against duplicate rows.
        supabase
          .from("direct_bids" as any)
          .select("id, bid_amount, message, status, submitted_at, withdraw_reason")
          .eq("direct_load_id", load.id)
          .eq("broker_id", user.id)
          .in("status", ["pending", "accepted"])
          .order("submitted_at", { ascending: false })
          .limit(1)
          .maybeSingle()
          .then(({ data }) => {
            if (!cancelled && data) setExistingBid(data as unknown as ExistingBid);
            if (!cancelled) setBidLoading(false);
          });
      }


      // Competitive bid info is FREE for all brokers — fetch it unconditionally.
      setBidRangeUnlocked(true);
      setExactCountUnlocked(true);
      setWinProbUnlocked(true);

      supabase.rpc("unlock_bid_insights" as any, {
        p_direct_load_id: load.id,
        p_unlock_type: "bid_range",
      }).then(({ data: result }) => {
        const r = result as any;
        if (!cancelled && r?.success && r.min_bid != null) {
          setUnlockedBidRange({ min: r.min_bid, max: r.max_bid });
        }
      });

      supabase.rpc("unlock_bid_insights" as any, {
        p_direct_load_id: load.id,
        p_unlock_type: "exact_count",
      }).then(({ data: result }) => {
        const r = result as any;
        if (!cancelled && r?.success && r.exact_count != null) {
          setUnlockedExactCount(r.exact_count);
        }
      });

      supabase.rpc("unlock_win_probability" as any, {
        p_direct_load_id: load.id,
        p_bid_amount: null,
      }).then(({ data: result }) => {
        const r = result as any;
        if (!cancelled && r?.success) setWinProb(r as WinProb);
      });
    } else {
      setBidLoading(false);
    }

    return () => { cancelled = true; };
  }, [load.id, user]);

  /* ── Bid market aggregates (REAL bids, aggregate only) ── */
  useEffect(() => {
    if (isQuote) return;
    let cancelled = false;
    supabase
      .rpc("get_direct_bid_market" as any, { _load_id: load.id })
      .then(({ data }) => {
        if (cancelled) return;
        const row: any = Array.isArray(data) ? data[0] : data;
        if (!row) return;
        setBidMarket({
          count: Number(row.bid_count ?? 0),
          min: row.min_bid != null ? Number(row.min_bid) : null,
          max: row.max_bid != null ? Number(row.max_bid) : null,
          median: row.median_bid != null ? Number(row.median_bid) : null,
          p25: row.p25_bid != null ? Number(row.p25_bid) : null,
          p75: row.p75_bid != null ? Number(row.p75_bid) : null,
        });
      });
    return () => { cancelled = true; };
  }, [load.id, isQuote, bidCount]);

  /* ── Live countdown timer (1s tick) ──────────────────── */

  useEffect(() => {
    if (!load.bid_deadline) return;
    const deadline = new Date(load.bid_deadline);

    const tick = () => {
      const now = new Date();
      const totalSecs = differenceInSeconds(deadline, now);
      if (totalSecs <= 0) {
        setCountdownStr(null);
        setCountdownUrgent(false);
        setCountdownLevel("normal");
        return;
      }
      const h = Math.floor(totalSecs / 3600);
      const m = Math.floor((totalSecs % 3600) / 60);
      const s = totalSecs % 60;
      setCountdownStr(`${h.toString().padStart(2, "0")}h ${m.toString().padStart(2, "0")}m ${s.toString().padStart(2, "0")}s`);
      setCountdownUrgent(totalSecs < 8 * 3600);
      setCountdownLevel(totalSecs <= 3600 ? "critical" : totalSecs <= 6 * 3600 ? "warn" : "normal");
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [load.bid_deadline]);

  /* ── Countdown color states: teal → yellow (≤6h) → red (≤1h) ── */
  const cdTone =
    countdownLevel === "critical"
      ? {
          box: "bg-gradient-to-br from-red-700 via-red-800 to-red-900",
          accent: "text-red-200",
          label: "Closing soon",
          divider: "bg-red-300/25",
          chip: "bg-red-400/15 ring-red-200/25",
        }
      : countdownLevel === "warn"
        ? {
            box: "bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700",
            accent: "text-amber-100",
            label: "Closing today",
            divider: "bg-amber-100/30",
            chip: "bg-amber-200/15 ring-amber-100/25",
          }
        : {
            box: "bg-gradient-to-br from-[#0B8A80] via-[#046D66] to-[#02514D]",
            accent: "text-teal-200",
            label: "Time to bid",
            divider: "bg-teal-200/25",
            chip: "bg-teal-300/10 ring-teal-200/20",
          };




  /* ── Realtime subscription for bid count ──────────────── */
  useEffect(() => {
    const refetchBidCount = async () => {
      const { data, count } = await supabase
        .from("direct_bids" as any)
        .select("id, submitted_at", { count: "exact", head: false })
        .eq("direct_load_id", load.id)
        .in("status", ["pending", "accepted"]);
      if (count !== null) setBidCount(count);
      if (data) {
        const tenMinAgo = new Date(Date.now() - 10 * 60_000);
        const sorted = (data as any[]).sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime());
        setRecentBidCount(sorted.filter(b => new Date(b.submitted_at) > tenMinAgo).length);
        if (sorted.length > 0) setLastBidTime(new Date(sorted[0].submitted_at));
      }
    };
    const channel = supabase
      .channel(`panel-bids-${load.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "direct_bids",
        filter: `direct_load_id=eq.${load.id}`,
      }, () => {
        setBidCount(c => c + 1);
        setRecentBidCount(c => c + 1);
        setLastBidTime(new Date());
        setFlashDots(true);
        setTimeout(() => setFlashDots(false), 1500);
      })
      .subscribe((status) => { if (status === "SUBSCRIBED") refetchBidCount(); });

    // Focus / visibility self-heal.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; refetchBidCount(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === "visible") runDebounced(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [load.id]);


  /* ── Bid submission ───────────────────────────────────── */
  const handleSubmitBid = async () => {
    if (!user || !bidAmount || submitting) return;
    setSubmitting(true);
    try {
      const rawAmount = parseFloat(bidAmount);
      const totalAmount = bidMode === "per_mile" && miles ? rawAmount * miles : rawAmount;

      const { data, error } = await supabase.rpc("submit_direct_bid" as any, {
        p_direct_load_id: load.id,
        p_bid_amount: totalAmount,
        p_message: bidMessage.trim() || null,
        p_ready_to_cover: readyToCover,
        p_truck_in_area: truckInArea,
        p_team_available: teamAvailable,
        p_eta_hours: etaHours ? parseInt(etaHours) : null,
        // How the broker quoted the rate — surfaced to the shipper on their bid card.
        p_rate_type: bidMode === "per_mile" ? "per_mile" : "flat",
      });

      if (error) {
        console.error("submit_direct_bid transport error", error);
        toast.error(error.message || "Failed to submit bid");
        return;
      }

      const result = data as { success?: boolean; error?: string; code?: string; bid_id?: string } | null;
      if (!result?.success) {
        const code = result?.code;
        const friendly =
          code === "verification_required"
            ? "Your company must complete verification before bidding."
            : code === "lrd_subscription_required"
              ? "An LR Direct Private subscription is required to bid on this load."
              : code === "lrd_full_required"
                ? "An LR Direct Full subscription is required for open-market loads."
                : code === "not_in_network"
                  ? "You're not in this shipper's private network yet."
                  : result?.error || "Failed to submit bid";
        console.warn("submit_direct_bid rejected", result);
        toast.error(friendly);
        return;
      }

      const resAny = result as any;
      if (resAny?.is_quote) {
        toast.success("Quote submitted");
        const { data: refQ } = await supabase
          .from("direct_quote_responses" as any)
          .select("id, rate, capacity_note, status, created_at, withdraw_reason")
          .eq("direct_load_id", load.id)
          .eq("broker_user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (refQ) setExistingQuote(refQ as any);
      } else {
        toast.success("Bid submitted successfully");
        const { data: refetched } = await supabase
          .from("direct_bids" as any)
          .select("id, bid_amount, message, status, submitted_at, withdraw_reason")
          .eq("direct_load_id", load.id)
          .eq("broker_id", user.id)
          .in("status", ["pending", "accepted"])
          .order("submitted_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (refetched) {
          setExistingBid(refetched as unknown as ExistingBid);
          setBidCount((c) => c + 1);
          // Shipper alert (bell + push) is fired server-side by the
          // tg_notify_shipper_on_direct_bid trigger, which resolves the load
          // owner via direct_loads.created_by. Nothing to do from the browser.
        }
      }



    } catch (err: any) {
      console.error("submit_direct_bid unexpected error", err);
      toast.error(err?.message || "Failed to submit bid");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Retract (withdraw) bid or quote — reason required ── */
  const [retracting, setRetracting] = useState(false);
  const [retractOpen, setRetractOpen] = useState(false);

  const handleRetractBid = async (reason: string) => {
    if (!existingBid || retracting) return;
    setRetracting(true);
    try {
      const { data, error } = await supabase.rpc("withdraw_direct_bid" as any, {
        p_bid_id: existingBid.id,
        p_reason: reason,
      });
      if (error) {
        console.error("withdraw_direct_bid transport error", error);
        toast.error(error.message || "Failed to retract bid");
        return;
      }
      const result = data as { success?: boolean; error?: string } | null;
      if (!result?.success) {
        toast.error(result?.error || "Failed to retract bid");
        return;
      }
      toast.success("Bid retracted");
      setRetractOpen(false);
      setExistingBid((prev) => (prev ? { ...prev, status: "withdrawn", withdraw_reason: reason } : prev));
      setBidCount((c) => Math.max(0, c - 1));
    } catch (err: any) {
      console.error("withdraw_direct_bid unexpected error", err);
      toast.error(err?.message || "Failed to retract bid");
    } finally {
      setRetracting(false);
    }
  };

  const handleRetractQuote = async (reason: string) => {
    if (!existingQuote || retracting) return;
    setRetracting(true);
    try {
      const { error } = await supabase.rpc("withdraw_direct_quote_response" as any, {
        p_response_id: existingQuote.id,
        p_reason: reason,
      });
      if (error) {
        console.error("withdraw_direct_quote_response error", error);
        toast.error(error.message || "Failed to retract quote");
        return;
      }
      toast.success("Quote retracted");
      setRetractOpen(false);
      setExistingQuote((prev) => (prev ? { ...prev, status: "withdrawn", withdraw_reason: reason } : prev));
    } catch (err: any) {
      console.error("withdraw_direct_quote_response unexpected error", err);
      toast.error(err?.message || "Failed to retract quote");
    } finally {
      setRetracting(false);
    }
  };

  const handleRetractConfirm = (reason: string) =>
    isQuote ? handleRetractQuote(reason) : handleRetractBid(reason);

  /* ── Recompute win probability when bid amount changes (free for all) ── */
  useEffect(() => {
    if (!user) return;
    const parsed = parseFloat(bidAmount);
    const p_bid_amount = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    const t = setTimeout(() => {
      supabase.rpc("unlock_win_probability" as any, {
        p_direct_load_id: load.id,
        p_bid_amount,
      }).then(({ data }) => {
        const r = data as any;
        if (r?.success) setWinProb(r as WinProb);
      });
    }, 400);
    return () => clearTimeout(t);
  }, [bidAmount, winProbUnlocked, user, load.id]);


  /* ── Derived values ───────────────────────────────────── */
  const deadlinePassed = load.bid_deadline ? new Date(load.bid_deadline) < new Date() : false;
  const { hasPrivateNetworkAccess, hasLRDirectFullAccess } = useBillingMode();
  const loadVisibility = (load as any).visibility_mode === "private_only" ? "private_only" : "open_market";
  // Tier-gate: blocks the bid form unless caller has the right tier.
  //   Private load  → needs any private-network tier (stripe/comped/lr_direct)
  //   Open market   → needs Full Access (stripe $99.99, comped, legacy lr_direct)

  const tierAllowsBid = isAdmin || isDemoUser
    ? true
    : loadVisibility === "private_only"
      ? hasPrivateNetworkAccess
      : hasLRDirectFullAccess;
  const activeBid = existingBid && existingBid.status !== "withdrawn" ? existingBid : null;
  const activeQuote = existingQuote && existingQuote.status !== "withdrawn" ? existingQuote : null;
  const canBid = !!user && !(isQuote ? activeQuote : activeBid) && !deadlinePassed && tierAllowsBid;
  const filledSlots = bidCount;

  // Distance is always resolved: multi-stop loads sum every leg in stop order,
  // simple loads use origin → destination. Computed + stored once if missing.
  const sortedStopsForRoute = useMemo(
    () => [...stops].sort((a, b) => (a.stop_number ?? 0) - (b.stop_number ?? 0)),
    [stops],
  );
  const firstStop = sortedStopsForRoute[0];
  const lastStop = sortedStopsForRoute[sortedStopsForRoute.length - 1];
  const { miles: resolvedMiles } = useResolvedLoadMiles({
    loadId: load.id,
    storedTotalMiles: load.total_miles,
    storedMilesSource: (load as any).total_miles_source ?? null,
    originLat: firstStop ? Number(firstStop.latitude) : null,
    originLng: firstStop ? Number(firstStop.longitude) : null,
    destLat: lastStop ? Number(lastStop.latitude) : null,
    destLng: lastStop ? Number(lastStop.longitude) : null,
    stops: sortedStopsForRoute,
  });
  const miles = resolvedMiles;

  const driveHours = miles ? Math.round((miles / 50) * 60) : null;
  const driveTimeStr = driveHours
    ? `${Math.floor(driveHours / 60)}h ${driveHours % 60}m Drive`
    : null;

  const rateMin = load.target_rate_min;
  const rateMax = load.target_rate_max;
  const suggestedRateStr = rateMin && rateMax
    ? `$${rateMin.toLocaleString()} – $${rateMax.toLocaleString()}`
    : rateMax
      ? `Up to $${rateMax.toLocaleString()}`
      : rateMin
        ? `From $${rateMin.toLocaleString()}`
        : null;
  const rpmMinStr = rateMin && miles && miles > 0 ? `$${(rateMin / miles).toFixed(2)}` : null;
  const rpmMaxStr = rateMax && miles && miles > 0 ? `$${(rateMax / miles).toFixed(2)}` : null;

  const stopsCount = stopsLoading ? "…" : String(stops.length || 2);

  /* ── Urgency derived values ───────────────────────────── */
  const pickupUrgency = useMemo(() => getPickupUrgency(load.pickup_date), [load.pickup_date]);
  const deliveryLabel = useMemo(() => getDeliveryLabel(load.delivery_date, load.pickup_date), [load.delivery_date, load.pickup_date]);
  
  const pickupStop = stops.find((s) => s.stop_type === "pickup");
  const deliveryStop = stops.find((s) => s.stop_type === "delivery" || s.stop_type === "dropoff");
  const pickupTimeWindow = getTimeWindow(pickupStop);
  const deliveryTimeWindow = getTimeWindow(deliveryStop);

  const hasShipperNotes = !!(load.shipper_notes_to_brokers || load.special_instructions);
  const handlingReqs = load.handling_requirements?.filter(Boolean) ?? [];
  const appointmentType = load.appointment_type || "fcfs";

  /* ── Competitive position ─────────────────────────────── */
  const competitivePos = useMemo(() => {
    const amt = parseFloat(bidAmount);
    if (!amt || isNaN(amt)) return null;
    const effectiveAmt = bidMode === "per_mile" && miles ? amt * miles : amt;
    return getCompetitivePosition(effectiveAmt, rateMin, rateMax);
  }, [bidAmount, bidMode, miles, rateMin, rateMax]);

  /* ── Last bid ago string ──────────────────────────────── */
  const lastBidAgoStr = useMemo(() => {
    if (!lastBidTime) return null;
    const mins = differenceInMinutes(new Date(), lastBidTime);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ago`;
  }, [lastBidTime, countdownStr /* re-eval on tick */]);

  /* ── Trust badges ─────────────────────────────────────── */
  const isVerifiedShipper = true; // On LR Direct, all shippers are verified
  const isFastPayout = load.priority_tag === "quick_pay" || load.priority_tag === "fast_pay";

  /* ═══════════════════════════════════════════════════════ */
  const cardClass = "bg-white border border-gray-200 rounded-lg p-3 w-full";
  const cardClassShadow = "bg-white border border-gray-200 rounded-lg p-3 w-full shadow-sm";

  /* ── Derived display values for the bid layout ──────────── */
  const pickCount = stops.filter((s) => s.stop_type === "pickup").length;
  const dropCount = stops.filter((s) => s.stop_type !== "pickup").length;
  const stopSummary = stops.length
    ? `${pickCount} Pick${pickCount === 1 ? "" : "s"} / ${dropCount} Drop${dropCount === 1 ? "" : "s"}`
    : null;
  const equipmentLabel = formatEquipmentType(load.equipment_type);
  // Cached ATLAS analysis stored on the load at post time. If a post path missed
  // generation, it is generated once on first broker view and cached server-side.
  const cachedAtlasAnalysis = useMemo(() => parseAtlasAnalysis((load as any).atlas_analysis), [(load as any).atlas_analysis]);
  const atlasAnalysis = useEnsureAtlasLoadAnalysis(load.id, cachedAtlasAnalysis, !isQuote);

  // Aggregate-only shipper activity (numbers, no identity) — one call per panel load
  const { data: shipperActivity, isLoading: shipperActivityLoading } = useDirectShipperActivity(load.id);

  const enteredAmount = (() => {
    const n = parseFloat(bidAmount);
    if (!Number.isFinite(n) || n <= 0) return null;
    return bidMode === "per_mile" && miles ? n * miles : n;
  })();
  const enteredRpm = enteredAmount && miles && miles > 0 ? enteredAmount / miles : null;
  const barLow = unlockedBidRange?.min ?? rateMin ?? null;
  const barHigh = unlockedBidRange?.max ?? rateMax ?? null;

  /* ── Bid-market intel derived from REAL aggregate bid stats ──
     Tiered by bid volume so we never render a wall of "—":
       0 bids → empty state, 1 bid → low bid only,
       2 bids → low + range + position/gap, 3+ → adds median + concentration. */
  const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
  const bidIntel = useMemo(() => {
    const count = bidMarket?.count ?? unlockedExactCount ?? 0;
    const low = bidMarket?.min ?? unlockedBidRange?.min ?? null;
    const high = bidMarket?.max ?? unlockedBidRange?.max ?? null;
    const median = bidMarket?.median ?? null;
    const p25 = bidMarket?.p25 ?? null;
    const p75 = bidMarket?.p75 ?? null;

    let positionBand: string | null = null;
    if (count >= 1 && enteredAmount != null && p25 != null && p75 != null) {
      positionBand = enteredAmount <= p25 ? "Lowest 25%" : enteredAmount >= p75 ? "Highest 25%" : "Middle";
    }
    let gapFromLow: string | null = null;
    if (count >= 1 && enteredAmount != null && low != null) {
      const diff = Math.round(enteredAmount - low);
      gapFromLow =
        diff > 0 ? `You're ${money(diff)} above current low`
          : diff < 0 ? `You're ${money(Math.abs(diff))} below current low`
            : "You match the current low";
    }
    const concentration =
      count >= 2 && p25 != null && p75 != null && Math.round(p75) > Math.round(p25)
        ? `Most bids are ${money(p25)}–${money(p75)}`
        : null;


    return { count, low, high, median, positionBand, gapFromLow, concentration };
  }, [bidMarket, unlockedBidRange, unlockedExactCount, enteredAmount]);

  /* ═══════════════════════════════════════════════════════ */
  /* QUOTE POSTS (post_type='quote') — forked mockup clone.   */
  /* Bid layout below is untouched.                           */
  /* ═══════════════════════════════════════════════════════ */
  const { data: quoteShipperName } = useNetworkShipperName(
    isQuote ? ((load as any).private_network_id ?? null) : null,
  );

  const viewerIsShipper =
    profile?.user_type === "shipper" || (!!user && (load as any).created_by === user.id);

  /* Aggregate-only quote market (low/avg/high) via SECURITY DEFINER RPC.
     Works for BOTH shippers and brokers — returns numbers only, never
     individual quotes or identities. */
  const [quoteMarket, setQuoteMarket] = useState<{
    count: number; low: number | null; avg: number | null; high: number | null;
  } | null>(null);

  useEffect(() => {
    if (!isQuote || !load.id) return;
    let cancelled = false;
    supabase
      .rpc("get_direct_quote_market" as any, { p_direct_load_id: load.id })
      .then(({ data }) => {
        if (cancelled) return;
        const row: any = Array.isArray(data) ? data[0] : data;
        setQuoteMarket({
          count: Number(row?.quote_count ?? 0),
          low: row?.low_rate != null ? Number(row.low_rate) : null,
          avg: row?.avg_rate != null ? Number(row.avg_rate) : null,
          high: row?.high_rate != null ? Number(row.high_rate) : null,
        });
      });
    return () => { cancelled = true; };
  }, [isQuote, load.id]);

  const shipperMarketInsights = useMemo(() => {
    if (!isQuote) return null;
    if (!quoteMarket) return { low: null, avg: null, high: null };
    return { low: quoteMarket.low, avg: quoteMarket.avg, high: quoteMarket.high };
  }, [isQuote, quoteMarket]);




  if (isQuote) {
    return (
      <>
        <DirectQuoteDetailView
          load={load}
          stops={stops as any}
          stopsLoading={stopsLoading}
          miles={miles}
          requestedBy={
            inShipperPrivateNetwork && quoteShipperName && quoteShipperName !== "Private Network"
              ? quoteShipperName
              : null
          }
          deadlineCountdown={countdownStr}
          deadlinePassed={deadlinePassed}
          quoteLoading={bidLoading}
          existingQuote={existingQuote}
          retracting={retracting}
          onRetract={() => setRetractOpen(true)}
          rate={bidAmount}
          onRateChange={setBidAmount}
          notes={bidMessage}
          onNotesChange={setBidMessage}
          submitting={submitting}
          canSubmit={canBid}
          onSubmit={() => showWarningOrRun(handleSubmitBid)}
          gate={
            !!user && !tierAllowsBid ? (
              <BidActionGate visibilityMode={loadVisibility} loadId={load.id} />
            ) : !user ? (
              <span className="text-xs text-muted-foreground">Sign in as a broker to quote.</span>
            ) : undefined
          }
          marketInsights={shipperMarketInsights}
          notifiedCount={null}
          viewerIsShipper={viewerIsShipper}
        />
        <VerificationWarningModal {...modalProps} />
        <RetractReasonDialog
          open={retractOpen}
          onOpenChange={setRetractOpen}
          kind="quote"
          submitting={retracting}
          onConfirm={handleRetractConfirm}
        />
      </>
    );
  }


  /* ═══════════════════════════════════════════════════════ */
  /* NEW BID-LOAD LAYOUT (mockup clone). Quote posts keep the */
  /* original layout below — untouched in this pass.          */
  /* ═══════════════════════════════════════════════════════ */
  if (!isQuote) {
    const bidBody = bidLoading ? (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-12 w-full bg-muted/50" />
        <Skeleton className="h-8 w-full bg-muted/50" />
      </div>
    ) : existingBid && existingBid.status === "withdrawn" ? (
      <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-3">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-muted-foreground" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Bid retracted</span>
        </div>
        <div className="text-2xl font-bold tabular-nums text-muted-foreground line-through">
          ${Number(existingBid.bid_amount).toLocaleString()}
        </div>
        {existingBid.withdraw_reason && (
          <p className="text-[11px] text-muted-foreground">Reason: {existingBid.withdraw_reason}</p>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setExistingBid(null)}
          className="mt-1 h-8 w-fit text-xs"
        >
          Place a new bid
        </Button>
      </div>
    ) : activeBid ? (
      <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex items-center gap-2">
          {activeBid.status === "accepted" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          {activeBid.status === "pending" && <Clock className="h-4 w-4 text-amber-600" />}
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {activeBid.status === "accepted" ? "Awarded — you won" : "Bid pending review"}
          </span>
        </div>
        <div className="text-2xl font-bold tabular-nums text-foreground">
          ${Number(activeBid.bid_amount).toLocaleString()}
        </div>
        {activeBid.message && <p className="text-[11px] text-muted-foreground">{activeBid.message}</p>}
        <span className="text-[10px] text-muted-foreground">
          Submitted {format(new Date(activeBid.submitted_at), "MMM d, h:mm a")}
        </span>
        {activeBid.status === "pending" && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRetractOpen(true)}
            disabled={retracting}
            className="mt-1 h-8 w-fit border-border bg-transparent text-xs text-foreground hover:bg-muted/60 hover:text-foreground"
          >
            {retracting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <XCircle className="mr-1.5 h-3.5 w-3.5" />}
            Retract bid
          </Button>
        )}
      </div>
    ) : deadlinePassed ? (
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-900">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        Bid deadline has passed
      </div>
    ) : !!user && !tierAllowsBid ? (
      <div className="rounded-lg bg-white p-3">
        <BidActionGate visibilityMode={loadVisibility} loadId={load.id} />
      </div>
    ) : canBid ? (
      <div className="flex flex-col gap-3">
        {/* Amount + rate type */}
        <div className="mx-auto flex w-full max-w-[360px] items-stretch gap-2 rounded-xl border border-border bg-background p-2">
          <div className="relative flex-1 min-w-0">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-2xl font-bold text-foreground">$</span>
            <Input
              type="number"
              placeholder={bidMode === "per_mile" ? "2.50" : "7,100"}
              value={bidAmount}
              onChange={(e) => setBidAmount(e.target.value)}
              className={cn("h-14 border-0 pl-9 text-3xl font-bold shadow-none focus-visible:ring-0", NO_SPINNER)}
            />
          </div>
          <Select value={bidMode} onValueChange={(v) => setBidMode(v as "per_load" | "per_mile")}>
            <SelectTrigger className="h-14 w-[124px] shrink-0 text-xs bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="per_load">Flat Rate</SelectItem>
              <SelectItem value="per_mile">Per Mile</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Rate per mile + range bar */}
        <div className="px-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Rate per mile</span>
            <span className="text-xs font-bold text-brand-teal">
              {enteredRpm ? `$${enteredRpm.toFixed(2)} / mi` : "—"}
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full rounded-full bg-gradient-to-r from-destructive via-amber-400 to-emerald-500" />
          <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
            <span>LOW <span className="font-semibold text-foreground">{barLow ? `$${barLow.toLocaleString()}` : "—"}</span></span>
            {/* PLACEHOLDER — no market-average data source exists for LR Direct lanes */}
            <span>MARKET <span className="font-semibold text-muted-foreground/70">—</span></span>
            <span>HIGH <span className="font-semibold text-foreground">{barHigh ? `$${barHigh.toLocaleString()}` : "—"}</span></span>
          </div>
        </div>

        {/* Signal chips — real bid columns */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {[
            { label: "Ready to Cover", icon: CheckCircle2, on: readyToCover, set: setReadyToCover },
            { label: "Truck in Area", icon: Truck, on: truckInArea, set: setTruckInArea },
            { label: "Team Available", icon: Users, on: teamAvailable, set: setTeamAvailable },
          ].map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={() => c.set(!c.on)}
              className={cn(
                "flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2.5 text-[11px] font-semibold transition-colors",
                c.on
                  ? "border-brand-teal bg-brand-teal/10 text-brand-teal"
                  : "border-border bg-muted/30 text-muted-foreground hover:bg-muted/50",
              )}
            >
              <c.icon className="h-3.5 w-3.5" />
              {c.label}
            </button>
          ))}
        </div>

        {/* ETA + quick note — rebalanced to fill the empty right side with a real bid input */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-[11px] text-foreground">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              Send ETA <span className="text-muted-foreground">(optional)</span>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Input
                type="number"
                placeholder="—"
                value={etaHours}
                onChange={(e) => setEtaHours(e.target.value)}
                className={cn("h-8 w-16 bg-background border border-input text-center text-xs", NO_SPINNER)}
                min="1"
                max="72"
              />
              <span className="text-[11px] text-muted-foreground">hours to pickup</span>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5">
            <div className="flex items-center gap-1.5 text-[11px] text-foreground">
              <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
              Quick note <span className="text-muted-foreground">(optional)</span>
            </div>
            <div className="relative mt-2">
              <Input
                placeholder="Can pick up early, need appt..."
                value={bidMessage}
                onChange={(e) => setBidMessage(e.target.value.slice(0, 140))}
                className="h-8 bg-background border border-input pr-12 text-xs"
                maxLength={140}
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground">{bidMessage.length}/140</span>
            </div>
          </div>
        </div>


        {/* Competitive position — derived from shipper target range */}
        {competitivePos && (
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Competitive position</span>
              <span className="text-[10px] font-bold text-foreground">{competitivePos.label}</span>
            </div>
            <div className="flex gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={cn(
                    "h-1.5 flex-1 rounded-full transition-all",
                    i <= competitivePos.level ? positionColors[competitivePos.level] : "bg-muted",
                  )}
                />
              ))}
            </div>
          </div>
        )}

        <Button
          onClick={() => showWarningOrRun(handleSubmitBid)}
          disabled={submitting || !bidAmount}
          className="h-12 w-full bg-brand-teal text-sm font-bold uppercase tracking-wide text-white shadow-md hover:bg-brand-teal-deep"
        >
          {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          Submit bid
        </Button>
      </div>
    ) : (
      <span className="text-xs text-muted-foreground">Sign in as a broker to bid.</span>
    );

    return (
      <div className="flex flex-col gap-3 border-t border-gray-200 bg-white p-3 md:p-4">

        {/* ── Private freight banner (full-width, top of panel) ── */}
        {loadVisibility === "private_only" && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-brand-teal/20 bg-brand-teal/10 px-4 py-3">
            <Lock className="h-4 w-4 text-brand-teal" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-brand-teal">Private freight</span>
            <span className="text-[11px] text-brand-teal/80">
              {inShipperPrivateNetwork
                ? "Posted by a shipper in your curated network."
                : "Posted by a shipper in your curated network. Shipper details are revealed only after award."}
            </span>
            {!inShipperPrivateNetwork && (
              <span className="ml-auto rounded-full border border-brand-teal/30 bg-brand-teal/10 px-3 py-1 text-[11px] font-medium text-brand-teal">
                Award to unlock shipper details
              </span>
            )}

          </div>
        )}

        {/* ── Row 1 — four status tiles ─────────────────────── */}
        <DemoHint hintKey="section.urgentStatusBar">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {/* Time to bid — REAL (bid_deadline), mockup style */}
            <div className={cn("relative overflow-hidden rounded-2xl p-4 shadow-lg", cdTone.box)}>
              <Timer className="pointer-events-none absolute -bottom-6 -right-6 h-28 w-28 text-white/10" />
              <div className="relative flex items-start gap-3">
                <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1", cdTone.chip)}>
                  <Timer className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <span className={cn("block text-[10px] font-bold uppercase tracking-[0.18em]", cdTone.accent)}>
                    {cdTone.label}
                  </span>
                  {countdownStr ? (
                    <>
                      <div className="mt-0.5 text-2xl font-extrabold leading-none tracking-tight tabular-nums text-white">
                        {countdownStr}
                      </div>
                      <p className={cn("mt-1 text-xs font-semibold", cdTone.accent)}>Until bid deadline</p>
                    </>
                  ) : deadlinePassed ? (
                    <div className="mt-1 text-sm font-bold text-white">Bidding closed</div>
                  ) : (
                    <div className="mt-1 text-sm font-semibold text-white/80">No deadline set</div>
                  )}
                </div>
              </div>
              <div className={cn("relative my-3 h-px w-full", cdTone.divider)} />
              <div className="relative flex items-start gap-3">
                <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1", cdTone.chip)}>
                  <ShieldCheck className={cn("h-4 w-4", cdTone.accent)} />
                </div>
                <p className="text-[11px] leading-snug text-white">
                  <span className="block font-bold">Don't miss your chance.</span>
                  Bids received after the deadline may not be considered.
                </p>
              </div>
            </div>


            {/* Live bid — subtle pale yellow tint matching Shipper credit rating */}
            <div className="flex flex-col rounded-[22px] border border-black/10 bg-[rgba(254,249,195,0.15)] p-4">
              <div className="flex items-center gap-2.5">
                <Zap className="h-5 w-5 text-[#02857E]" strokeWidth={2.25} />
                <span className="text-[15px] font-extrabold uppercase tracking-[0.12em] text-[#02857E]">
                  Live bid
                </span>
              </div>
              <p className="mt-2 text-[15px] font-medium leading-[1.35] tracking-[-0.01em] text-[#2D2A26]">
                Only bid if you have capacity in hand. If the shipper awards you the load,
                you're committed to cover it at your bid price.
              </p>
              <div className="mt-auto flex items-start justify-between gap-2 pt-3">
                {[
                  { Icon: Award, label: "Award" },
                  { Icon: FileCheck2, label: "Auto-fill\ncredit app" },
                  { Icon: Download, label: "Download" },
                  { Icon: Share2, label: "Get set up" },
                ].map(({ Icon, label }, i, arr) => (
                  <div key={label} className="flex flex-1 items-start gap-1.5">
                    <div className="flex flex-1 flex-col items-center gap-1 text-center">
                      <Icon className="h-4 w-4 text-[#4B5563]" strokeWidth={1.5} />
                      <span className="whitespace-pre-line text-[9px] leading-tight text-[#2D2A26]">{label}</span>
                    </div>
                    {i < arr.length - 1 && (
                      <ArrowRight className="mt-1.5 h-3 w-3 shrink-0 text-gray-600" strokeWidth={2} />
                    )}
                  </div>
                ))}
              </div>
            </div>


            {/* Shipper credit rating — cosmetic/hardwired */}
            <ShipperCreditRatingWidget loadId={load.id} />

            {/* About this shipper — REAL aggregate counts (numbers only, non-identifying).
                Occupies the former "Bid activity" slot; bid stats now live in Bid intelligence. */}
            <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-[#0B8A80] via-[#046D66] to-[#02514D] p-4">
              <div className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-white" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-white">About this shipper</span>
              </div>

              {shipperActivityLoading ? (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-12 rounded-lg bg-white/20" />
                  ))}
                </div>
              ) : shipperActivity ? (
                <>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {[
                      { label: "Active loads", value: shipperActivity.activeLoads },
                      { label: "Loads posted", value: shipperActivity.totalLoads },
                      { label: "Quotes posted", value: shipperActivity.quotesPosted },
                    ].map((s) => (
                      <div key={s.label} className="rounded-lg bg-white/15 px-2 py-1.5 text-center">
                        <p className="text-lg font-bold tabular-nums leading-tight text-white">{s.value}</p>
                        <p className="text-[9px] font-semibold uppercase tracking-wider text-white/75">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {shipperActivity.verified && (
                    <div className="mt-2.5 flex items-start gap-2 rounded-lg bg-white/15 p-2.5">
                      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white" />
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-white">Verified shipper</p>
                        <p className="text-[11px] text-white/80">This shipper has been verified by LaneRunner</p>
                      </div>
                    </div>
                  )}
                  <p className="mt-2 text-[10px] leading-snug text-white/65">
                    Activity totals only — shipper identity stays hidden until award.
                  </p>
                </>
              ) : (
                <p className="mt-2 text-[11px] text-white/70">Shipper activity unavailable.</p>
              )}
            </div>

          </div>
        </DemoHint>

        {/* ── Row 2 — Load brief (REAL, only when recorded) ─── */}
        {load.brief_audio_path && (
          <LRDirectLoadBriefPlayer
            path={load.brief_audio_path}
            durationSec={load.brief_duration_seconds ?? null}
          />
        )}

        {/* ── Row 2.5 — Route tracker (REAL: direct_load_stops) ─ */}
        {!stopsLoading && (
          <RouteTrackerBar
            stops={sortedStopsForRoute}
            fallback={{
              originCity: load.origin_city,
              originState: load.origin_state,
              destCity: load.destination_city,
              destState: load.destination_state,
              pickupDate: load.pickup_date,
              deliveryDate: load.delivery_date,
            }}
            miles={miles}
          />
        )}


        {/* ── Row 4 — three columns ────────────────────────── */}
        <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)_minmax(0,1fr)]">

          {/* ═══ LEFT — Load overview + Shipper comments (all REAL) ═══ */}
          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-1.5">
              <Route className="h-3.5 w-3.5 text-primary" />
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Load overview</h4>
            </div>

            <div className="text-lg font-bold leading-tight text-foreground">
              {load.origin_city}, {load.origin_state} <span className="text-muted-foreground">→</span> {load.destination_city}, {load.destination_state}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {load.load_type && (
                <Badge variant="outline" className="h-5 border-brand-teal-deep/25 bg-brand-teal-deep/5 px-2 py-0 text-[10px] font-semibold uppercase text-brand-teal-deep">
                  {load.load_type}
                </Badge>
              )}
              <Badge variant="outline" className="h-5 border-blue-200 bg-blue-50 px-2 py-0 text-[10px] font-semibold text-blue-700">
                {equipmentLabel}
              </Badge>
              {(load as any).load_nature && (
                <Badge variant="outline" className="h-5 border-violet-200 bg-violet-50 px-2 py-0 text-[10px] font-semibold capitalize text-violet-700">
                  {(load as any).load_nature}
                </Badge>
              )}
              {stopSummary && (
                <span className="text-[11px] text-muted-foreground">{stopSummary}</span>
              )}
            </div>

            {/* Pickup / delivery */}
            <div className="mt-3 space-y-2.5 border-t border-border pt-3">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-teal-deep" />
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Pickup</p>
                  <p className={cn("text-xs font-semibold", pickupUrgency.isUrgent ? "text-destructive" : "text-foreground")}>
                    {pickupUrgency.label}
                    {pickupTimeWindow && <span className="font-normal text-muted-foreground"> · {pickupTimeWindow}</span>}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{load.origin_city}, {load.origin_state}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Delivery</p>
                  <p className="text-xs font-semibold text-foreground">
                    {deliveryLabel}
                    <span className="font-normal uppercase text-muted-foreground"> · {appointmentType === "appointment" ? "Appointment" : "FCFS"}</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground">{load.destination_city}, {load.destination_state}</p>
                </div>
              </div>
            </div>

            {/* Spec table */}
            <dl className="mt-3 divide-y divide-border border-t border-border text-xs">
              {[
                { icon: Route, label: "Distance", value: miles ? `${miles.toLocaleString()} mi` : "—" },
                { icon: Weight, label: "Weight", value: load.weight_lbs ? `${Number(load.weight_lbs).toLocaleString()} lbs` : "—" },
                { icon: Package, label: "Commodity", value: load.commodity || "—" },
                { icon: Truck, label: "Equipment", value: `${equipmentLabel}${load.length_ft ? ` ${load.length_ft}'` : ""}` },
                // Requirements maps to direct_loads.handling_requirements (the
                // Hazmat / Team / etc. chips on the post form). Reference maps to
                // direct_loads.reference_number. Both rows are omitted entirely
                // when the shipper left them blank, instead of a permanent "—".
                ...(handlingReqs.length
                  ? [{ icon: ClipboardCheck, label: "Requirements", value: handlingReqs.join(" · ") }]
                  : []),
                ...(load.reference_number
                  ? [{ icon: FileText, label: "Reference", value: load.reference_number }]
                  : []),
              ].map((r) => (
                <div key={r.label} className="flex items-center justify-between gap-3 py-1.5">
                  <dt className="flex items-center gap-1.5 text-muted-foreground">
                    <r.icon className="h-3 w-3" />
                    <span className="text-[10px] uppercase tracking-wider">{r.label}</span>
                  </dt>
                  <dd className="min-w-0 truncate text-right font-semibold text-foreground">{r.value}</dd>
                </div>
              ))}
            </dl>


            {/* Verified shipper badge moved into the Shipper activity box (right column) */}
            </div>



            {/* Shipper comments — REAL */}
            <div className="flex flex-grow flex-col rounded-xl border border-gray-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5 text-primary" />
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Shipper comments</h4>
              </div>
              {load.shipper_notes_to_brokers ? (
                <p className="text-[11px] leading-relaxed text-foreground">{load.shipper_notes_to_brokers}</p>
              ) : load.special_instructions ? (
                <p className="text-[11px] leading-relaxed text-foreground">{load.special_instructions}</p>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center gap-1 py-4 text-center">
                  <MessageSquare className="h-6 w-6 text-muted-foreground/50" />
                  <p className="text-[11px] text-muted-foreground">No comments from shipper</p>
                </div>
              )}
            </div>
          </div>

          {/* ═══ MIDDLE — Place your bid + Atlas load analysis ═══ */}
          <div className="flex flex-col gap-3">
          <div className="w-full overflow-hidden rounded-xl border-2 border-brand-teal bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
              <h4 className="flex items-center justify-center gap-1.5 text-base font-bold uppercase tracking-wide text-foreground">
                Place your bid <Zap className="h-4 w-4 text-brand-teal" />
              </h4>
              {countdownStr && !deadlinePassed && (
                <span className="flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold tabular-nums text-foreground">
                  <Timer className="h-3 w-3" />
                  {countdownStr} left
                </span>
              )}
            </div>
            <p className="mb-3 text-center text-[11px] text-muted-foreground">Enter your all-in rate for this load</p>
            {bidBody}
          </div>

          {/* Atlas load analysis — cached at post time, no per-view AI call.
              Hidden entirely when the load has no stored analysis. */}
          {atlasAnalysis && <AtlasLoadAnalysisCard analysis={atlasAnalysis} className="mt-auto" />}
          </div>


          {/* ═══ RIGHT — Bid intelligence + partners ═══ */}
          <div className="flex flex-col gap-3">

            {/* Bid intelligence — REAL (ungated RPCs) */}
            <DemoHint hintKey="section.winProbability">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="flex items-center gap-1.5 border-b border-border px-3 py-2.5">
                  <BarChart3 className="h-3.5 w-3.5 text-primary" />
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Bid intelligence</h4>
                </div>
                <dl className="divide-y divide-border text-xs">
                  {/* 1. Current low bid */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <DollarSign className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Current low bid</span>
                    </dt>
                    <dd className={cn("font-bold tabular-nums", bidIntel.low != null ? "text-foreground" : "text-muted-foreground")}>
                      {bidIntel.low != null ? money(bidIntel.low) : "No bids yet"}
                    </dd>
                  </div>
                  {/* 2. Current median bid */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <BarChart3 className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Current median bid</span>
                    </dt>
                    <dd className={cn("font-bold tabular-nums", bidIntel.median != null ? "text-foreground" : "text-muted-foreground")}>
                      {bidIntel.median != null ? money(bidIntel.median) : "—"}
                    </dd>
                  </div>
                  {/* 3. Bid range */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <DollarSign className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Bid range</span>
                    </dt>
                    <dd className={cn("text-right font-bold tabular-nums", bidIntel.low != null && bidIntel.high != null ? "text-foreground" : "text-muted-foreground")}>
                      {bidIntel.low != null && bidIntel.high != null
                        ? `${money(bidIntel.low)}–${money(bidIntel.high)}`
                        : "—"}
                    </dd>
                  </div>
                  {/* 4. Price concentration */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <Activity className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Price concentration</span>
                    </dt>
                    <dd className={cn("text-right font-bold tabular-nums", bidIntel.concentration ? "text-foreground" : "text-muted-foreground")}>
                      {bidIntel.concentration ?? "—"}
                    </dd>
                  </div>
                  {/* 5. Your position */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <TrendingUp className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Your position</span>
                    </dt>
                    <dd className={cn("text-right font-bold", bidIntel.positionBand ? "text-foreground" : "text-muted-foreground")}>
                      {bidIntel.positionBand ?? (enteredAmount == null ? "Enter a rate" : "—")}
                    </dd>
                  </div>
                  {/* 6. Distance from low bid */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <DollarSign className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Distance from low bid</span>
                    </dt>
                    <dd className={cn("text-right font-bold tabular-nums", bidIntel.gapFromLow ? "text-foreground" : "text-muted-foreground")}>
                      {bidIntel.gapFromLow ?? (bidIntel.low == null ? "No bids yet" : "Enter a rate")}
                    </dd>
                  </div>
                  {/* 7. Active bidders */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Active bidders</span>
                    </dt>
                    <dd className={cn("font-bold", bidIntel.count > 0 ? "text-foreground" : "text-muted-foreground")}>
                      {bidIntel.count > 0
                        ? `${bidIntel.count} broker${bidIntel.count === 1 ? "" : "s"} bidding`
                        : "No bids yet"}
                    </dd>
                  </div>
                  {/* 8. Win probability */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <TrendingUp className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Win probability</span>
                    </dt>
                    <dd className={cn("font-bold tabular-nums", winProb?.mode === "scored" ? "text-foreground" : "text-muted-foreground")}>
                      {winProb?.mode === "scored" ? `${winProb.score}%` : "Enter a rate"}
                    </dd>
                  </div>
                  {/* 9. Rate basis */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <FileText className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Rate basis</span>
                    </dt>
                    <dd className="font-bold text-foreground">
                      All-in {bidMode === "per_mile" ? "per mile" : "flat rate"}
                    </dd>
                  </div>
                  {/* 10. Deadline */}
                  <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <dt className="flex items-center gap-1.5 text-muted-foreground">
                      <Timer className="h-3 w-3" />
                      <span className="text-[10px] uppercase tracking-wider">Deadline</span>
                    </dt>
                    <dd className={cn("text-right font-bold tabular-nums", deadlinePassed ? "text-destructive" : "text-foreground")}>
                      {load.bid_deadline
                        ? format(new Date(load.bid_deadline), "MMM d, h:mm a")
                        : deadlinePassed ? "Closed" : "—"}
                    </dd>
                  </div>
                  {suggestedRateStr && (
                    <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <dt className="flex items-center gap-1.5 text-muted-foreground">
                        <DollarSign className="h-3 w-3" />
                        <span className="text-[10px] uppercase tracking-wider">Shipper target</span>
                      </dt>
                      <dd className="text-right">
                        <span className="block font-bold text-foreground">{suggestedRateStr}</span>
                        {rpmMinStr && rpmMaxStr && (
                          <span className="text-[10px] text-muted-foreground">{rpmMinStr}–{rpmMaxStr}/mi</span>
                        )}
                      </dd>
                    </div>
                  )}
                </dl>


              </div>
            </DemoHint>

            {/* About this shipper moved into the 4-tile status grid at the top. */}




            {/* Bid checklist — REAL (form state) */}
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="mb-2 flex items-center gap-1.5">
                <ClipboardCheck className="h-3.5 w-3.5 text-primary" />
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Bid checklist</h4>
              </div>
              <div className="flex flex-col gap-1.5">
                {[
                  { done: !!bidAmount && !isNaN(parseFloat(bidAmount)), label: "Rate entered" },
                  { done: readyToCover || truckInArea || teamAvailable, label: "Signal selected" },
                  { done: bidMessage.trim().length > 0, label: "Message added" },
                  { done: !!etaHours, label: "ETA provided" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2 text-xs">
                    {item.done ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    ) : (
                      <div className="h-3.5 w-3.5 shrink-0 rounded-full border border-muted-foreground/40" />
                    )}
                    <span className={cn("font-medium", item.done ? "text-foreground" : "text-muted-foreground")}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sureline Quick Pay — REAL partner link */}
            <div className="mt-auto rounded-xl border border-gray-200 bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <img src={surelineLogo} alt="Sureline Capital" className="h-5 object-contain" />
                <span className="rounded-full bg-muted/50 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
                  Preferred partner
                </span>
              </div>
              <p className="text-[11px] font-semibold text-foreground">Sureline Quick Pay</p>
              <p className="text-[11px] text-muted-foreground">Get paid faster with SureLine.</p>
              <ul className="mt-2 space-y-1">
                {["Advance rates up to 97%", "Funding in as little as 24 hours", "5-minute application"].map((t) => (
                  <li key={t} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Check className="h-3 w-3 shrink-0 text-emerald-600" />
                    {t}
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                className="mt-2 h-9 w-full justify-between text-xs"
                onClick={() => {
                  logSurelineClick({ user, profile, source: "lr_direct", loadId: load.id });
                  window.open("https://surelinecapital.xenplatforms.com/sign-up?rf=130", "_blank", "noopener,noreferrer");
                }}
              >
                Get funded now
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>

        {inShipperPrivateNetwork ? (
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3 w-3 text-brand-teal-deep" />
            You're in this shipper's private network.
          </p>
        ) : (
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" />
            You won't see the shipper's name or contact until you win this load.
          </p>
        )}

        {/* Leaderboard banner slot — desktop only, random Pistoresi ad per mount */}
        <div className="hidden md:block">
          <a
            href="https://pistoresilogistics.com/home"
            target="_blank"
            rel="noopener noreferrer"
            data-slot="lr-direct-leaderboard-banner"
            onClick={() => { supabase.functions.invoke("track-pistoresi-click", { body: { variant: pistoresiAd === pistoresiAd1 ? "ad-1" : "ad-2", load_id: (load as any)?.id ?? null } }).catch(() => {}); }}
            className="w-full max-w-[970px] mx-auto aspect-[970/90] max-h-[90px] flex items-center justify-center rounded-md overflow-hidden"
          >
            <img src={pistoresiAd.url} alt="Pistoresi Logistics" className="w-full h-full object-contain" />
          </a>
        </div>

        <VerificationWarningModal {...modalProps} />
        <RetractReasonDialog
          open={retractOpen}
          onOpenChange={setRetractOpen}
          kind={isQuote ? "quote" : "bid"}
          submitting={retracting}
          onConfirm={handleRetractConfirm}
        />
      </div>
    );
  }

  return (

    <div className="flex flex-col border-t border-gray-200 bg-white">

      {/* ════════════════════════════════════════════════════ */}
      {/* URGENT STATUS BAR — Full width across top          */}
      {/* ════════════════════════════════════════════════════ */}
      <DemoHint hintKey="section.urgentStatusBar">
        {isQuote ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 md:p-4 border-b border-gray-200">
          {/* Countdown */}
          <div className={cn(
            cardClass,
            countdownUrgent && "border-destructive/40 bg-destructive/5"
          )}>
            <div className="flex items-center gap-2">
              <Timer className={cn("h-3.5 w-3.5 shrink-0", countdownUrgent ? "text-destructive" : "text-muted-foreground")} />
              {countdownStr ? (
                <div>
                  {countdownUrgent && (
                    <span className="text-[9px] font-bold text-destructive uppercase tracking-wider block leading-none mb-0.5">
                      Urgent: Closes Soon
                    </span>
                  )}
                  <span className={cn(
                    "text-xs font-bold tabular-nums",
                    countdownUrgent ? "text-destructive" : "text-foreground"
                  )}>
                    {countdownStr}
                  </span>
                </div>
              ) : deadlinePassed ? (
                <span className="text-xs font-semibold text-destructive">Bidding Closed</span>
              ) : (
                <span className="text-xs text-muted-foreground">No deadline set</span>
              )}
            </div>
          </div>

          {/* Competition */}
          <div className={cardClass}>
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div>
                <span className="text-[9px] text-muted-foreground uppercase tracking-wider block leading-none mb-0.5">Competition</span>
                <span className="text-xs font-bold text-foreground">{filledSlots} Broker{filledSlots !== 1 ? "s" : ""} Bidding</span>
              </div>
            </div>
          </div>

          {/* Availability tile removed — bid cap no longer applies. */}


          {/* Activity */}
          <div className={cardClass}>
            <div className="flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <div>
                <span className="text-[9px] text-muted-foreground uppercase tracking-wider block leading-none mb-0.5">Activity</span>
                <span className="text-xs font-bold text-foreground">
                  {lastBidAgoStr ? `Last bid ${lastBidAgoStr}` : "No bids yet"}
                </span>
              </div>
            </div>
          </div>
        </div>
        ) : (
        /* ── BID LOADS: redesigned status strip ─────────────── */
        <div className="flex flex-col gap-3 p-3 md:p-4 border-b border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">

            {/* Countdown — hero tile (mockup style) */}
            <div className={cn("relative overflow-hidden rounded-2xl p-4 w-full shadow-lg", cdTone.box)}>
              <Timer className="pointer-events-none absolute -bottom-6 -right-6 h-28 w-28 text-white/10" />
              <div className="relative flex items-start gap-3">
                <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1", cdTone.chip)}>
                  <Timer className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <span className={cn("block text-[10px] font-bold uppercase tracking-[0.18em]", cdTone.accent)}>
                    {cdTone.label}
                  </span>
                  {countdownStr ? (
                    <>
                      <div className="mt-0.5 text-2xl font-extrabold leading-none tracking-tight tabular-nums text-white">
                        {countdownStr}
                      </div>
                      <p className={cn("mt-1 text-xs font-semibold", cdTone.accent)}>Until bid deadline</p>
                    </>
                  ) : deadlinePassed ? (
                    <div className="mt-1 text-sm font-bold text-white">Bidding closed</div>
                  ) : (
                    <div className="mt-1 text-sm font-semibold text-white/80">No deadline set</div>
                  )}
                </div>
              </div>
              <div className={cn("relative my-3 h-px w-full", cdTone.divider)} />
              <div className="relative flex items-start gap-3">
                <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1", cdTone.chip)}>
                  <ShieldCheck className={cn("h-4 w-4", cdTone.accent)} />
                </div>
                <p className="text-[11px] leading-snug text-white">
                  <span className="block font-bold">Don't miss your chance.</span>
                  Bids received after the deadline may not be considered.
                </p>
              </div>
            </div>


            {/* Instructions — what a live bid means */}
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 w-full">
              <div className="flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700">
                  Live bid
                </span>
              </div>
              <p className="mt-1.5 text-[11px] leading-snug text-amber-900">
                Only bid if you have capacity in hand. If the shipper awards you the load,
                you're committed to cover it at your bid price.
              </p>
            </div>

            {/* Shipper credit rating — cosmetic/hardwired */}
            <ShipperCreditRatingWidget loadId={load.id} />

            {/* Activity */}
            <div className={cn(cardClass, "rounded-xl")}>
              <div className="flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Bid activity
                </span>
              </div>
              <div className="mt-1.5 text-sm font-bold text-foreground">
                {lastBidAgoStr ? `Last bid ${lastBidAgoStr}` : "No bids yet"}
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">
                {lastBidAgoStr ? "Competition is active" : "Be the first to bid"}
              </p>
            </div>
          </div>

          {/* Voice brief — full width, only when present */}
          {load.brief_audio_path && (
            <LRDirectLoadBriefPlayer
              path={load.brief_audio_path}
              durationSec={load.brief_duration_seconds ?? null}
            />
          )}
        </div>
        )}
      </DemoHint>


      {/* ════════════════════════════════════════════════════ */}
      {/* 3-COLUMN CONTENT                                    */}
      {/* ════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 md:p-4 bg-white items-start">

        {/* ════════════════════════════════════════════════════ */}
        {/* LEFT COLUMN — Trip + Equipment + Market Signal     */}
        {/* ════════════════════════════════════════════════════ */}
        <div className="flex flex-col gap-3">

          {/* ── Trust Badges ────────────────────────────────── */}
          <div className={cn(cardClass, "flex flex-wrap gap-1.5")}>
            {isVerifiedShipper && (
              <Badge variant="outline" className="text-[10px] px-2 py-0 h-5 font-semibold text-emerald-700 border-emerald-300 bg-emerald-50">
                <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />
                Verified Shipper
              </Badge>
            )}
            {isFastPayout && (
              <Badge variant="outline" className="text-[10px] px-2 py-0 h-5 font-semibold text-primary border-primary/30 bg-primary/5">
                <DollarSign className="h-2.5 w-2.5 mr-0.5" />
                Fast Payout
              </Badge>
            )}
            <Badge
              variant={pickupUrgency.isUrgent ? "destructive" : "secondary"}
              className="text-[10px] px-2 py-0 h-5 font-semibold"
            >
              {pickupUrgency.isUrgent && <Zap className="h-2.5 w-2.5 mr-0.5" />}
              {pickupUrgency.label}
            </Badge>
            <Badge variant="outline" className="text-[10px] px-2 py-0 h-5 text-muted-foreground uppercase">
              {appointmentType === "appointment" ? "Appointment" : "FCFS"}
            </Badge>
          </div>

          {/* ── Trip Overview ──────────────────────────────── */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-foreground">Trip Overview</h4>
              {load.reference_number && (
                <span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                  Ref: {load.reference_number}
                </span>
              )}
            </div>

            <div className="flex items-start gap-2 mb-1.5">
              <MapPin className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
              <div>
                <span className="text-xs font-semibold text-foreground">
                  {load.origin_city}, {load.origin_state}
                </span>
                <span className="text-[10px] text-muted-foreground ml-1">(Origin)</span>
                <div className="flex items-center gap-1 mt-0.5">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <span className={cn("text-[11px]", pickupUrgency.isUrgent ? "text-destructive font-semibold" : "text-muted-foreground")}>
                    Pickup: {pickupUrgency.label}
                    {pickupTimeWindow && ` (${pickupTimeWindow})`}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 mb-2">
              <MapPin className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
              <div>
                <span className="text-xs font-semibold text-foreground">
                  {load.destination_city}, {load.destination_state}
                </span>
                <span className="text-[10px] text-muted-foreground ml-1">(Destination)</span>
                <div className="flex items-center gap-1 mt-0.5">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[11px] text-muted-foreground">
                    Delivery: {deliveryLabel}
                    {deliveryTimeWindow && ` (${deliveryTimeWindow})`}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-xs text-muted-foreground border-t border-border pt-2">
              <Route className="h-3.5 w-3.5 shrink-0" />
              <span>
                {miles ? `${miles.toLocaleString()} mi` : "—"}
                {driveTimeStr && ` · ${driveTimeStr}`}
              </span>
            </div>
          </div>

          {/* ── Equipment ──────────────────────────────────── */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Truck className="h-3.5 w-3.5 text-primary" />
                <h4 className="text-sm font-semibold text-foreground">Equipment</h4>
              </div>
              <div className="flex items-center gap-1.5">
                {load.load_type && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">
                    {load.load_type}
                  </Badge>
                )}
                {(load as any).load_nature && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 capitalize">
                    {(load as any).load_nature}
                  </Badge>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div className="text-muted-foreground">Type</div>
              <div className="font-medium text-foreground text-right">
                {load.equipment_type?.replace("_", " ") || "Van"}{load.length_ft ? ` — ${load.length_ft} ft` : ""}
              </div>
              <div className="text-muted-foreground">Commodity</div>
              <div className="font-medium text-foreground text-right">{load.commodity || "General"}</div>
              <div className="text-muted-foreground">Weight</div>
              <div className="font-medium text-foreground text-right">
                {load.weight_lbs ? `${Number(load.weight_lbs).toLocaleString()} lbs` : "—"}
              </div>
              <div className="text-muted-foreground">Stops</div>
              <div className="font-medium text-foreground text-right">{stopsCount}</div>
            </div>
          </div>

          {/* ── Market Signal Card — real signals only ─────── */}
          {(!!(miles && miles > 400) || recentBidCount >= 3) && (
          <DemoHint hintKey="section.marketSignal">
            <div className={cardClass}>
              <div className="flex items-center gap-1.5 mb-2">
                <TrendingUp className="h-3.5 w-3.5 text-primary" />
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Market Signal</h4>
              </div>
              <div className="flex flex-col gap-1.5">
                {miles && miles > 400 && (
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
                    <span className="text-xs font-medium text-foreground">Long Haul</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{miles.toLocaleString()} mi</span>
                  </div>
                )}
                {recentBidCount >= 3 && (
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-destructive shrink-0 animate-pulse" />
                    <span className="text-xs font-semibold text-destructive">Heating Up</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">{recentBidCount} bids in 10 min</span>
                  </div>
                )}
              </div>
            </div>
          </DemoHint>
          )}

          {/* ── Pricing Insights Card ───────────────────────── */}
          {suggestedRateStr && (
          <DemoHint hintKey="section.pricingInsights">
            <div className={cardClass}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <BarChart3 className="h-3.5 w-3.5 text-primary" />
                  <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Pricing Insights</h4>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Shipper Target Rate</span>
                  <span className="text-xs font-bold text-foreground">{suggestedRateStr}</span>
                </div>
                {rpmMinStr && rpmMaxStr && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Per Mile</span>
                    <span className="text-xs font-bold text-foreground">{rpmMinStr}–{rpmMaxStr}</span>
                  </div>
                )}
              </div>
            </div>
          </DemoHint>
          )}


          {/* ── Notes from Shipper ──────────────────────────── */}
          {hasShipperNotes && (
            <div className={cardClass}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Notes from Shipper</h4>
              </div>
              {load.shipper_notes_to_brokers && (
                <p className="text-xs text-foreground leading-relaxed">{load.shipper_notes_to_brokers}</p>
              )}
              {load.special_instructions && (
                <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                  <span className="font-medium">Instructions:</span> {load.special_instructions}
                </p>
              )}
            </div>
          )}

          {/* ── Voice Load Brief (quotes only — bid loads show it lengthwise up top) ── */}
          {isQuote && load.brief_audio_path && (
            <LRDirectLoadBriefPlayer
              path={load.brief_audio_path}
              durationSec={load.brief_duration_seconds ?? null}
            />
          )}



          {stops.length > 0 && (() => {
            const hasAnyPrivate = stops.some(
              (s) => s.pickup_number || s.pallet_count != null || s.confidential_notes,
            );
            const unlocked = stops.some((s) => s.is_unlocked);
            // Hide entirely if nothing private was set and viewer isn't unlocked
            if (!hasAnyPrivate && !unlocked) return null;
            return (
              <div className={cardClass}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    {unlocked ? (
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      Stop Dispatch
                    </h4>
                  </div>
                  {unlocked ? (
                    <Badge className="text-[9px] px-1.5 py-0 h-4 bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">
                      Unlocked
                    </Badge>
                  ) : (
                    <Badge className="text-[9px] px-1.5 py-0 h-4 bg-muted text-muted-foreground border-border" variant="outline">
                      Awarded broker only
                    </Badge>
                  )}
                </div>

                {unlocked && (
                  <p className="text-[10px] text-emerald-700 mb-2 leading-relaxed">
                    You won this load — treat this as your dispatch sheet.
                  </p>
                )}
                {!unlocked && (
                  <p className="text-[10px] text-muted-foreground mb-2 leading-relaxed">
                    Pickup numbers and confidential notes are released to the awarded broker only.
                  </p>
                )}

                <div className="space-y-1.5">
                  {stops.map((s) => {
                    const isPickup = s.stop_type === "pickup";
                    const label = isPickup ? "Pickup #" : "PRO #";
                    return (
                      <div
                        key={s.id}
                        className="rounded-md border border-border px-2 py-1.5 bg-muted/30"
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={cn(
                            "inline-flex items-center justify-center h-4 w-4 rounded text-[9px] font-bold uppercase",
                            isPickup ? "bg-[hsl(var(--brand-teal-deep)/0.15)] text-[hsl(var(--brand-teal-deep))]" : "bg-destructive/10 text-destructive"
                          )}>
                            {isPickup ? "P" : "D"}
                          </span>
                          <span className="text-[10px] font-medium text-foreground truncate">
                            Stop {s.stop_number} — {s.city}, {s.state}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] pl-5">
                          <span className="text-muted-foreground">{label}</span>
                          <span className={cn("font-medium text-right", !unlocked && "blur-sm select-none")}>
                            {unlocked ? (s.pickup_number || "—") : "••••••"}
                          </span>
                          <span className="text-muted-foreground">Pallets</span>
                          <span className={cn("font-medium text-right", !unlocked && "blur-sm select-none")}>
                            {unlocked ? (s.pallet_count ?? "—") : "••"}
                          </span>
                          {(unlocked ? s.confidential_notes : true) && (
                            <>
                              <span className="text-muted-foreground col-span-2 mt-0.5">Notes</span>
                              <span className={cn("col-span-2 text-foreground leading-relaxed", !unlocked && "blur-sm select-none")}>
                                {unlocked ? (s.confidential_notes || "—") : "•••• •••••• ••••"}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}


          {handlingReqs.length > 0 && (
            <div className={cn(cardClass, "flex flex-wrap gap-1.5")}>
              {handlingReqs.map((req) => (
                <Badge key={req} variant="outline" className="text-[10px] px-2 py-0 h-5 text-muted-foreground uppercase">
                  {req}
                </Badge>
              ))}
            </div>
          )}

          {/* Lane History card removed — its metrics were hardcoded placeholders. */}

        </div>

        {/* ════════════════════════════════════════════════════ */}
        {/* MIDDLE COLUMN — Place Your Bid                     */}
        {/* ════════════════════════════════════════════════════ */}
        <div className="flex flex-col gap-3">

          {/* ── Place Your Bid card ────────────────────────── */}
          <div className={cardClassShadow}>
            {load.visibility_mode === "private_only" && (
              <div className="mb-3">
                <PrivateNetworkBadge networkId={load.private_network_id} variant="banner" />
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-base font-bold text-foreground">{isQuote ? "Submit Quote" : "Place Your Bid"}</h4>
              {countdownStr && !deadlinePassed && (
                <Badge
                  variant={countdownUrgent ? "destructive" : "secondary"}
                  className="text-[10px] px-2 py-0.5 h-5 font-semibold gap-1 tabular-nums"
                >
                  <Timer className="h-3 w-3" />
                  {countdownStr}
                </Badge>
              )}
            </div>

            {bidLoading ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : isQuote && existingQuote ? (
              <div className="flex flex-col gap-2">
                <Badge variant="secondary" className="text-xs w-fit">Quote submitted</Badge>
                <div className="text-xl font-bold text-foreground">${Number(existingQuote.rate).toLocaleString()}</div>
                {existingQuote.capacity_note && (
                  <p className="text-xs text-muted-foreground">{existingQuote.capacity_note}</p>
                )}
                <span className="text-[10px] text-muted-foreground">
                  Sent {format(new Date(existingQuote.created_at), "MMM d, h:mm a")}
                </span>
                <p className="text-[10px] text-muted-foreground italic">
                  Quotes are indicative pricing. The shipper may convert this into a live load and open bidding.
                </p>
              </div>
            ) : existingBid ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {existingBid.status === "accepted" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  {existingBid.status === "rejected" && <XCircle className="h-4 w-4 text-destructive" />}
                  {existingBid.status === "pending" && <Clock className="h-4 w-4 text-amber-500" />}
                  <Badge
                    variant={existingBid.status === "accepted" ? "default" : existingBid.status === "rejected" ? "destructive" : "secondary"}
                    className="text-xs"
                  >
                    {existingBid.status === "accepted" ? "Awarded — You Won" : existingBid.status === "rejected" ? "Not Selected" : existingBid.status === "withdrawn" ? "Retracted" : "Pending Review"}
                  </Badge>
                </div>
                <div className="text-xl font-bold text-foreground">
                  ${Number(existingBid.bid_amount).toLocaleString()}
                </div>
                {existingBid.message && (
                  <p className="text-xs text-muted-foreground">{existingBid.message}</p>
                )}
                {existingBid.status === "withdrawn" && existingBid.withdraw_reason && (
                  <p className="text-xs text-muted-foreground">Reason: {existingBid.withdraw_reason}</p>
                )}
                <span className="text-[10px] text-muted-foreground">
                  Submitted {format(new Date(existingBid.submitted_at), "MMM d, h:mm a")}
                </span>
                {existingBid.status === "pending" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRetractOpen(true)}
                    disabled={retracting}
                    className="mt-1 h-8 text-xs border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    {retracting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <XCircle className="h-3.5 w-3.5 mr-1.5" />}
                    Retract Bid
                  </Button>
                )}
              </div>
            ) : deadlinePassed ? (
              <div className="flex items-center gap-2 text-xs text-destructive">
                <AlertTriangle className="h-4 w-4" />
                Bid deadline has passed
              </div>
            ) : !!user && !tierAllowsBid ? (

              <BidActionGate visibilityMode={loadVisibility} loadId={load.id} />
            ) : canBid ? (
              <div className="flex flex-col gap-3">
                <p className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {filledSlots === 0
                    ? "Be first to bid — no competition yet"
                    : filledSlots < 3
                      ? `You're competing with ${filledSlots} broker${filledSlots !== 1 ? "s" : ""}`
                      : `High competition — ${filledSlots} brokers bidding`}
                </p>

                {/* Currency input + per load/mile toggle */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg font-bold text-foreground">$</span>
                    <Input
                      type="number"
                      placeholder={bidMode === "per_mile" ? "2.50" : "7,100"}
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      className="pl-8 h-11 text-lg font-bold"
                    />
                  </div>
                  <Select value={bidMode} onValueChange={(v) => setBidMode(v as "per_load" | "per_mile")}>
                    <SelectTrigger className="w-[100px] h-11 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_load">per load</SelectItem>
                      <SelectItem value="per_mile">per mile</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Auto-calc display for per-mile mode */}
                {bidMode === "per_mile" && miles && bidAmount && (
                  <p className="text-[10px] text-muted-foreground -mt-1.5">
                    Total: <span className="font-semibold text-foreground">${(parseFloat(bidAmount) * miles).toLocaleString()}</span> ({miles.toLocaleString()} mi)
                  </p>
                )}

                {/* Suggested rate range */}
                {suggestedRateStr && (
                  <p className="text-[11px] text-muted-foreground -mt-1">
                    Suggested: <span className="font-semibold text-foreground">{suggestedRateStr}</span>
                    {rpmMinStr && rpmMaxStr && ` (${rpmMinStr}–${rpmMaxStr}/mi)`}
                  </p>
                )}

                {/* ── Competitive Position Indicator ───────── */}
                <DemoHint hintKey="section.competitivePosition">
                  <div className="rounded-md bg-muted/30 px-3 py-2 border border-border">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Competitive Position</span>
                      {competitivePos && (
                        <span className={cn(
                          "text-[10px] font-bold",
                          competitivePos.level >= 3 ? "text-emerald-600" :
                          competitivePos.level >= 2 ? "text-amber-600" : "text-destructive"
                        )}>
                          {competitivePos.label}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={cn(
                            "h-2 flex-1 rounded-full transition-all duration-300",
                            competitivePos && i <= competitivePos.level
                              ? positionColors[competitivePos.level]
                              : "bg-muted"
                          )}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[8px] text-muted-foreground">Too High</span>
                      <span className="text-[8px] text-muted-foreground">Very Strong</span>
                    </div>
                  </div>
                </DemoHint>

                {/* Checkbox row — structured signals */}
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={readyToCover} onCheckedChange={(v) => setReadyToCover(!!v)} className="h-3.5 w-3.5" />
                    Ready to Cover
                  </label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={truckInArea} onCheckedChange={(v) => setTruckInArea(!!v)} className="h-3.5 w-3.5" />
                    Truck in Area
                  </label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <Checkbox checked={teamAvailable} onCheckedChange={(v) => setTeamAvailable(!!v)} className="h-3.5 w-3.5" />
                    Team Available
                  </label>
                </div>

                {/* ETA to Pickup */}
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">Available in</span>
                  <Input
                    type="number"
                    placeholder="—"
                    value={etaHours}
                    onChange={(e) => setEtaHours(e.target.value)}
                    className="h-7 w-16 text-xs text-center"
                    min="1"
                    max="72"
                  />
                  <span className="text-[11px] text-muted-foreground">hours</span>
                </div>

                {/* Free-text message */}
                <div className="relative">
                  <MessageSquare className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Can pick up early, Need appointment confirmation..."
                    value={bidMessage}
                    onChange={(e) => setBidMessage(e.target.value.slice(0, 140))}
                    className="pl-8 h-8 text-xs"
                    maxLength={140}
                  />
                  <span className="absolute right-2.5 top-2 text-[9px] text-muted-foreground">{bidMessage.length}/140</span>
                </div>

                {/* SUBMIT COMPETITIVE BID button */}
                <Button
                  onClick={() => showWarningOrRun(handleSubmitBid)}
                  disabled={submitting || !bidAmount}
                  className="w-full h-12 text-sm font-bold uppercase tracking-wide bg-primary text-white hover:bg-primary/90 shadow-md"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {isQuote ? "Submit Quote" : "Submit Competitive Bid"}
                </Button>

                {/* Bids only */}

                {isQuote && (
                  <p className="text-[11px] italic text-muted-foreground text-center -mt-1">
                    Indicative pricing only — this is not a commitment.
                  </p>
                )}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">Sign in as a broker to bid.</span>
            )}
          </div>

          {/* ── Live Bid Activity — real data only, hidden for quote posts ── */}
          {!isQuote && (
          <div className={cn(cardClassShadow, "p-0 overflow-hidden border-primary/20")}>
            <div className="bg-primary/10 border-b border-primary/20 px-3 py-2 flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5 text-primary" />
              <h4 className="text-[11px] font-bold text-primary uppercase tracking-wider">Live Bid Activity</h4>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              <div className="rounded-md bg-muted/40 border border-border px-2.5 py-2">
                <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5">Bids Placed</p>
                <p className="text-base font-bold text-foreground tabular-nums">
                  {unlockedExactCount ?? filledSlots}
                </p>
              </div>
              <div className="rounded-md bg-muted/40 border border-border px-2.5 py-2">
                <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5">Last Bid</p>
                <p className="text-base font-bold text-foreground">{lastBidAgoStr ?? "—"}</p>
              </div>
              <div className="rounded-md bg-muted/40 border border-border px-2.5 py-2 col-span-2">
                <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5">Bid Range</p>
                <p className="text-sm font-bold text-foreground tabular-nums">
                  {unlockedBidRange
                    ? `$${unlockedBidRange.min.toLocaleString()} – $${unlockedBidRange.max.toLocaleString()}`
                    : "No bids yet"}
                </p>
              </div>
              {countdownStr && !deadlinePassed && (
                <div className={cn(
                  "rounded-md px-2.5 py-2 col-span-2 border flex items-center gap-1.5",
                  countdownUrgent ? "bg-destructive/10 border-destructive/30" : "bg-primary/5 border-primary/20"
                )}>
                  <Timer className={cn("h-3.5 w-3.5 shrink-0", countdownUrgent ? "text-destructive" : "text-primary")} />
                  <span className={cn(
                    "text-xs font-semibold tabular-nums",
                    countdownUrgent ? "text-destructive" : "text-primary"
                  )}>
                    {countdownStr} until bidding closes
                  </span>
                </div>
              )}
            </div>
          </div>
          )}


          {/* View Full Detail link — quote posts only (removed for bid loads) */}
          {isQuote && (
            <div className={cardClass}>
              <Link
                to={`/lr-direct/opportunity/${load.id}`}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1 w-fit"
              >
                View Full Detail <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          )}

        </div>

        {/* ════════════════════════════════════════════════════ */}
        {/* RIGHT COLUMN — Credits + Bid Insights + Win Prob   */}
        {/* ════════════════════════════════════════════════════ */}
        <div className="flex flex-col gap-3">

          {/* Credits balance lives in TopBar (CompanyCreditsBadge) — single source of truth. */}


          {/* ── Don't Lose This Load — wired ──────────────── */}
          <div className={cn(cardClassShadow, "p-0 overflow-hidden")}>
            <div className="bg-primary px-3 py-2.5 flex items-center gap-2">
              <Activity className="h-4 w-4 text-white" />
              <h4 className="text-sm font-bold text-white">Don't Lose This Load</h4>
            </div>

            <div className="p-3 flex flex-col gap-3">
              <p className="text-xs font-medium text-foreground leading-relaxed">
                See if your bid is too high or too low
              </p>

              {bidRangeUnlocked && unlockedBidRange && (
                <div className="bg-muted/50 rounded-md p-2.5 border border-border">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Bid Range</p>
                  <p className="text-sm font-bold text-foreground">
                    ${unlockedBidRange.min.toLocaleString()} – ${unlockedBidRange.max.toLocaleString()}
                  </p>
                </div>
              )}

              {bidRangeUnlocked && !unlockedBidRange && (
                <div className="bg-muted/50 rounded-md p-2.5 border border-border">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Bid Range</p>
                  <p className="text-sm font-bold text-foreground">No bids yet</p>
                </div>
              )}

              {exactCountUnlocked && (
                <div className="bg-muted/50 rounded-md p-2.5 border border-border">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Exact Bid Count</p>
                  <p className="text-sm font-bold text-foreground">{unlockedExactCount ?? 0} brokers bidding</p>
                </div>
              )}

              {countdownStr && !deadlinePassed && (
                <div className={cn(
                  "rounded-md px-2.5 py-1.5 flex items-center gap-1.5 w-fit border",
                  countdownUrgent ? "bg-destructive/10 border-destructive/30" : "bg-accent/30 border-accent"
                )}>
                  <Clock className={cn("h-3.5 w-3.5", countdownUrgent ? "text-destructive" : "text-accent-foreground")} />
                  <span className={cn("text-xs font-semibold tabular-nums", countdownUrgent ? "text-destructive" : "text-accent-foreground")}>
                    {countdownStr} left to bid
                  </span>
                </div>
              )}


              <ul className="flex flex-col gap-1.5">
                <li className="flex items-center gap-2 text-xs text-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span className="font-medium">Avoid losing to a better-priced broker</span>
                </li>
                <li className="flex items-center gap-2 text-xs text-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span className="font-medium">Win more loads with better pricing</span>
                </li>
              </ul>
            </div>
          </div>

          {/* ── Win Probability Card ───────────────────────── */}
          <DemoHint hintKey="section.winProbability">
            <div className={cardClass}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5 text-primary" />
                  <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Win Probability</h4>
                </div>
                <Badge className="text-[9px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-primary/20" variant="outline">
                  Included
                </Badge>
              </div>


              {winProbUnlocked && winProb?.mode === "scored" && (
                <>
                  <div className="flex items-center justify-center py-3">
                    <span className={cn(
                      "text-3xl font-black tabular-nums",
                      winProb.score >= 66 ? "text-primary" : winProb.score >= 33 ? "text-foreground" : "text-destructive"
                    )}>
                      {winProb.score}%
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground text-center">
                    Your ${winProb.bid_amount.toLocaleString()} vs. {winProb.total_bids} bid{winProb.total_bids !== 1 ? "s" : ""}
                  </p>

                  <p className="text-[9px] text-muted-foreground/70 text-center mt-1">Updates as you change your bid</p>
                </>
              )}

              {winProbUnlocked && winProb?.mode === "pre_bid" && (
                <div className="py-2 space-y-1.5">
                  <p className="text-[10px] text-muted-foreground text-center mb-2">
                    Enter a bid amount above to see your personal win %
                  </p>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-muted-foreground">Bids placed</span>
                    <span className="font-bold text-foreground tabular-nums">{winProb.total_bids}</span>
                  </div>

                  {winProb.min_bid != null && (
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Range</span>
                      <span className="font-bold text-foreground tabular-nums">
                        ${Number(winProb.min_bid).toLocaleString()} – ${Number(winProb.max_bid).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {winProb.median_bid != null && (
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Median</span>
                      <span className="font-bold text-foreground tabular-nums">${Number(winProb.median_bid).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}

              {winProbUnlocked && !winProb && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </DemoHint>

          {/* ── Bid Checklist Card (flex-1 to fill) ────────── */}
          <div className={cardClass}>
            <div className="flex items-center gap-1.5 mb-2">
              <ClipboardCheck className="h-3.5 w-3.5 text-primary" />
              <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Bid Checklist</h4>
            </div>
            <div className="flex flex-col gap-1.5">
              {[
                { done: !!bidAmount && !isNaN(parseFloat(bidAmount)), label: "Rate entered" },
                { done: readyToCover || truckInArea || teamAvailable, label: "Signal selected" },
                { done: bidMessage.trim().length > 0, label: "Message added" },
                { done: !!etaHours, label: "ETA provided" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-xs">
                  {item.done ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  ) : (
                    <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/40 shrink-0" />
                  )}
                  <span className={cn("font-medium", item.done ? "text-foreground" : "text-muted-foreground")}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Sureline Capital — Get Paid Faster */}
          <div className={cardClassShadow}>
            <div className="flex items-center justify-between mb-3">
              <img src={surelineLogo} alt="Sureline Capital" className="h-6 object-contain" />
              <p className="text-[11px] text-muted-foreground">Exclusive for LaneRunner users</p>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium bg-muted/50 px-2 py-0.5 rounded-full">Preferred Partner</span>
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Get Paid Faster</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
              {[
                "Turning Receivables Into Cash",
                "Simple & Clear Fee Structure",
                "5-Minute Application Process",
                "Funding In As Little As 24 Hours",
                "Advance Rates Up To 97%",
                "Dedicated Account Support",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            <Button
              className="w-full h-10 font-medium"
              onClick={() => {
                logSurelineClick({
                  user,
                  profile,
                  source: "lr_direct",
                  loadId: load.id,
                });
                window.open("https://surelinecapital.xenplatforms.com/sign-up?rf=130", "_blank", "noopener,noreferrer");
              }}
            >
              Get Funded Now
            </Button>
          </div>

          {/* Credit App Fill panel intentionally NOT rendered here — it belongs
              only on the awarded broker's post-award surfaces (Opportunity page
              + BrokerDispatchView), not on the board's inline detail panel. */}
        </div>
      </div>

      {/* Leaderboard banner slot — desktop only, random Pistoresi ad per mount */}
      <div className="hidden md:block px-4 pb-4">
        <a
          href="https://pistoresilogistics.com/home"
          target="_blank"
          rel="noopener noreferrer"
          data-slot="lr-direct-leaderboard-banner"
            onClick={() => { supabase.functions.invoke("track-pistoresi-click", { body: { variant: pistoresiAd === pistoresiAd1 ? "ad-1" : "ad-2", load_id: (load as any)?.id ?? null } }).catch(() => {}); }}
          className="w-full max-w-[970px] mx-auto aspect-[970/90] max-h-[90px] flex items-center justify-center rounded-md overflow-hidden"
        >
          <img
            src={pistoresiAd.url}
            alt="Pistoresi Logistics"
            className="w-full h-full object-contain"
          />
        </a>
      </div>
      <VerificationWarningModal {...modalProps} />
        <RetractReasonDialog
          open={retractOpen}
          onOpenChange={setRetractOpen}
          kind={isQuote ? "quote" : "bid"}
          submitting={retracting}
          onConfirm={handleRetractConfirm}
        />
    </div>
  );
}
